import React, { useState, useEffect } from 'react';
import { canManageFinance } from '@/lib/permissions';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Users, Wallet, ArrowUpCircle, ArrowDownCircle, Percent, Edit, Trash2, History, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable wrapper for each investor row. Lives outside Investors so it's not
// recreated on every parent re-render. All handlers + computed values come via props.
const SortableInvestorRow = ({
  investor,
  effectivePercentage,
  labaBerjalan,
  canManageInvestors,
  formatCurrency,
  onEdit,
  onTransfer,
  onDelete,
  onViewHistory,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: investor.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`investor-row-${investor.id}`}>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {canManageInvestors && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700"
              data-testid={`investor-drag-handle-${investor.id}`}
              aria-label="Drag untuk mengurutkan investor"
            >
              <GripVertical size={16} />
            </div>
          )}
          <span>{investor.name}</span>
        </div>
      </TableCell>
      <TableCell className="text-right font-semibold text-slate-600">
        {formatCurrency(investor.opening_balance || 0)}
      </TableCell>
      <TableCell className="text-right font-semibold text-green-600">
        {investor.period_deposits > 0 ? '+' : ''}{formatCurrency(investor.period_deposits || 0)}
      </TableCell>
      <TableCell className="text-right font-semibold text-red-600">
        {investor.period_withdrawals > 0 ? '-' : ''}{formatCurrency(investor.period_withdrawals || 0)}
      </TableCell>
      <TableCell
        className={`text-right font-bold cursor-pointer hover:underline ${(investor.closing_balance || 0) >= 0 ? 'text-teal-600' : 'text-red-600'}`}
        onClick={() => onViewHistory(investor)}
      >
        {formatCurrency(investor.closing_balance || 0)}
      </TableCell>
      <TableCell className="text-right font-semibold text-slate-700">
        {(investor.share_percentage || 0).toFixed(2)}%
      </TableCell>
      <TableCell className="text-right font-bold text-teal-700">
        {effectivePercentage.toFixed(2)}%
      </TableCell>
      <TableCell className={`text-right font-bold ${labaBerjalan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency(Math.trunc(labaBerjalan))}
      </TableCell>
      <TableCell
        className={`text-right font-bold ${((investor.closing_balance || 0) + Math.trunc(labaBerjalan)) >= 0 ? 'text-purple-700' : 'text-red-600'}`}
        data-testid={`investor-saldo-next-period-${investor.id}`}
      >
        {formatCurrency((investor.closing_balance || 0) + Math.trunc(labaBerjalan))}
      </TableCell>
      {canManageInvestors && (
        <TableCell className="text-right">
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(investor)}
              data-testid={`investor-edit-${investor.id}`}
            >
              <Edit size={14} />
            </Button>
            <Button
              variant="default"
              size="sm"
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => onTransfer(investor)}
              data-testid={`investor-transfer-${investor.id}`}
            >
              <Wallet size={14} className="mr-1" />
              Transfer
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(investor)}
              data-testid={`investor-delete-${investor.id}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

const Investors = ({ user }) => {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [investorTransactions, setInvestorTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyViewMode, setHistoryViewMode] = useState('last10');
  const [historyFilter, setHistoryFilter] = useState({ startDate: '', endDate: '' });
  const [maxLevel, setMaxLevel] = useState(1);  // Track max level
  const [totalDividenInvestor, setTotalDividenInvestor] = useState(0);  // Total dividen investor from dashboard
  
  // Delete Dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteInvestor, setDeleteInvestor] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    share_percentage: '',
    level: 1
  });
  
  const [transferForm, setTransferForm] = useState({
    investor_id: '',
    account_type: '',
    account_id: '',
    amount: '',
    transfer_type: 'DEPOSIT',
    description: '',
    transfer_date: new Date().toISOString().split('T')[0]
  });

  // For account selection
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);

  // Period selection - listen to global period changes from Layout
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  useEffect(() => {
    fetchPeriods();
    fetchAccountsData();

    // Get initial period from localStorage (set by Layout)
    const savedPeriodId = localStorage.getItem('selectedPeriodId');
    if (savedPeriodId) {
      setSelectedPeriod(savedPeriodId);
    }
  }, []);

  // Listen for period changes from Layout sidebar
  usePeriodChange((event) => {
    const { periodId } = event.detail;
    setSelectedPeriod(periodId);
  });

  useEffect(() => {
    // Only fetch investors when period is selected
    if (selectedPeriod) {
      fetchInvestors();
    }
  }, [selectedPeriod, periods]);


  const fetchPeriods = async () => {
    try {
      const response = await api.get('/periods');
      setPeriods(response.data);
      
      // If no period selected yet, get from localStorage or use first period
      if (!selectedPeriod) {
        const savedPeriodId = localStorage.getItem('selectedPeriodId');
        if (savedPeriodId && response.data.find(p => p.id === savedPeriodId)) {
          setSelectedPeriod(savedPeriodId);
        } else if (response.data.length > 0) {
          const activePeriod = response.data.find(p => p.is_active);
          const periodId = activePeriod ? activePeriod.id : response.data[0].id;
          setSelectedPeriod(periodId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
    }
  };

  const fetchInvestors = async () => {
    try {
      const url = selectedPeriod 
        ? `/investors/balance-by-period?period_id=${selectedPeriod}`
        : '/investors/balance-by-period';
      const response = await api.get(url);
      setInvestors(response.data);
      
      // Calculate max level
      if (response.data.length > 0) {
        const max = Math.max(...response.data.map(inv => inv.level || 1));
        setMaxLevel(max);
      }
      
      // Fetch total dividen investor from dashboard data
      fetchDividenInvestor();
    } catch (error) {
      toast.error('Gagal memuat data investor');
    } finally {
      setLoading(false);
    }
  };

  const fetchDividenInvestor = async () => {
    try {
      const periodId = selectedPeriod;
      if (!periodId) return;
      
      // Periode TERTUTUP: pakai nilai dividen investor yang dibekukan saat tutup buku,
      // sama seperti Dashboard. Saham mitra bisa berubah setelah periode ditutup, jadi
      // perhitungan live akan menggeser pembagian mitra vs investor.
      try {
        const snapRes = await api.get(`/periods/${periodId}/snapshot`);
        const snap = snapRes.data;
        if (snap?.has_snapshot && snap.total_investor_dividend !== undefined && snap.total_investor_dividend !== null) {
          setTotalDividenInvestor(snap.total_investor_dividend);
          return;
        }
      } catch (snapError) {
        console.error('Failed to fetch period snapshot for dividen investor:', snapError);
      }
      
      // Fetch all needed data (same as Dashboard)
      const [salesRes, purchasesRes, expensesRes, centralExpensesRes, branchesRes, managersRes] = await Promise.all([
        api.get(`/sales?period_id=${periodId}`),
        api.get(`/purchases?period_id=${periodId}`),
        api.get(`/expenses?period_id=${periodId}`),
        api.get(`/central-expenses?period_id=${periodId}`),
        api.get('/branches'),
        api.get('/managers')
      ]);
      
      const sales = salesRes.data;
      const purchases = purchasesRes.data;
      const expenses = expensesRes.data;
      const centralExpenses = centralExpensesRes.data;
      const branches = branchesRes.data;
      const managersData = managersRes.data;
      
      // Central income (negative amounts) and expenses (positive amounts)
      const centralRevenue = centralExpenses
        .filter(e => (e.amount || 0) < 0)
        .reduce((sum, e) => sum + Math.abs(e.amount || 0), 0);
      const centralActualExpenses = centralExpenses
        .filter(e => (e.amount || 0) > 0)
        .reduce((sum, e) => sum + (e.amount || 0), 0);
      const pusatProfit = centralRevenue - centralActualExpenses;
      
      // Calculate per-branch dividend (matches Dashboard logic exactly)
      const getBranchShareholders = (branchId) => {
        const shareholders = [];
        managersData.forEach(mgr => {
          const share = (mgr.branch_shares || []).find(s => s.branch_id === branchId);
          if (share) {
            shareholders.push({
              manager_id: mgr.id,
              percentage: share.percentage || 0,
              position: mgr.position
            });
          }
        });
        return shareholders;
      };
      
      let totalInvestorDividend = pusatProfit; // PUSAT goes 100% to investors
      const withManager = [];
      const withoutManager = [];
      
      branches.forEach(branch => {
        const branchSales = sales.filter(s => s.branch_id === branch.id);
        const branchPurchases = purchases.filter(p => p.branch_id === branch.id);
        const branchExpenses = expenses.filter(e => e.branch_id === branch.id);
        
        const revenue = branchSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const purchaseCost = branchPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        const expenseCost = branchExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        const profitLoss = Math.round(revenue - purchaseCost - expenseCost);
        
        const shareholders = getBranchShareholders(branch.id);
        const mitraOnly = shareholders.filter(s => s.position === 'Mitra');
        const managersInBranch = shareholders.filter(s => s.position === 'Manager');
        
        const mitraDividend = mitraOnly.reduce(
          (sum, m) => sum + Math.round((m.percentage / 100) * profitLoss),
          0
        );
        const profitAfterMitra = profitLoss - mitraDividend;
        const sisaDividen = profitAfterMitra;
        
        if (managersInBranch.length > 0) {
          withManager.push({ sisaDividen, managersInBranch });
        } else {
          withoutManager.push({ investorDividend: sisaDividen });
        }
      });
      
      // For branches with manager: investor share = sisa - pembagian_manager
      const totalSisaDividenManager = withManager.reduce((sum, b) => sum + b.sisaDividen, 0);
      const managerPercentage = withManager.length > 0 && withManager[0].managersInBranch.length > 0
        ? withManager[0].managersInBranch[0].percentage
        : 0;
      const pembagianManager = Math.round((managerPercentage / 100) * totalSisaDividenManager);
      const sisaDividenAfterManager = totalSisaDividenManager - pembagianManager;
      
      const totalDividenInvestorNonManager = withoutManager.reduce((sum, b) => sum + b.investorDividend, 0);
      totalInvestorDividend += totalDividenInvestorNonManager + sisaDividenAfterManager;
      
      setTotalDividenInvestor(totalInvestorDividend);
    } catch (error) {
      console.error('Failed to fetch dividen investor:', error);
    }
  };

  const fetchAccountsData = async () => {
    try {
      const [branchesRes, managersRes, centralRes, otherRes, customerRes] = await Promise.all([
        api.get('/branches'),
        api.get('/managers'),
        api.get('/central-accounts'),
        api.get('/other-accounts'),
        api.get('/customer-accounts')
      ]);
      
      setBranches(branchesRes.data);
      setManagers(managersRes.data);
      
      // Prepare accounts list
      const allAccounts = [];
      
      // Central
      centralRes.data.forEach(acc => {
        allAccounts.push({
          id: acc.id,
          name: acc.account_name,
          type: 'CENTRAL',
          balance: acc.balance
        });
      });
      
      // Hutang Usaha
      otherRes.data.forEach(acc => {
        allAccounts.push({
          id: acc.id,
          name: acc.account_name,
          type: 'HUTANG_USAHA',
          balance: acc.balance
        });
      });
      
      // Branch
      branchesRes.data.forEach(branch => {
        (branch.accounts || []).forEach(acc => {
          allAccounts.push({
            id: acc.id,
            name: `${branch.name} - ${acc.account_name}`,
            type: 'BRANCH',
            balance: acc.balance
          });
        });
      });
      
      // Manager
      managersRes.data.forEach(manager => {
        (manager.accounts || []).forEach((acc, idx) => {
          allAccounts.push({
            id: acc.id || `${manager.id}_${idx}`,
            name: `${manager.name} - ${acc.account_name}`,
            type: 'MANAGER',
            balance: acc.balance
          });
        });
      });
      
      // Customer
      customerRes.data.forEach(acc => {
        allAccounts.push({
          id: acc.id,
          name: `${acc.customer_name} - ${acc.account_name}`,
          type: 'CUSTOMER',
          balance: acc.balance
        });
      });
      
      setAccounts(allAccounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      if (editingInvestor) {
        await api.put(`/investors/${editingInvestor.id}`, formData);
        toast.success('Investor berhasil diperbarui');
      } else {
        await api.post('/investors', formData);
        toast.success('Investor berhasil ditambahkan');
      }

      setDialogOpen(false);
      resetForm();
      fetchInvestors();
    } catch (error) {
      toast.error(editingInvestor ? 'Gagal memperbarui investor' : 'Gagal menambah investor');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    
    // Use selected period from navigation
    if (!selectedPeriod) {
      toast.error('Tidak ada periode yang dipilih. Silakan pilih periode di dropdown terlebih dahulu.');
      return;
    }
    
    if (loading) return;
    setLoading(true);

    try {
      await api.post('/investors/transfer', {
        ...transferForm,
        amount: parseFloat(transferForm.amount),
        period_id: selectedPeriod
      });
      
      const periodName = periods.find(p => p.id === selectedPeriod)?.name || '';
      toast.success(`${transferForm.transfer_type === 'DEPOSIT' ? 'Deposit' : 'Withdraw'} berhasil untuk periode ${periodName}`);
      setTransferDialogOpen(false);
      resetTransferForm();
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan transfer');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (investor) => {
    setEditingInvestor(investor);
    setFormData({
      name: investor.name,
      share_percentage: investor.share_percentage || 0,
      level: investor.level || 1
    });
    setDialogOpen(true);
  };

  const openTransferDialog = (investor) => {
    // Get default date based on selected period
    let defaultDate = new Date().toISOString().split('T')[0];
    
    if (selectedPeriod) {
      const period = periods.find(p => p.id === selectedPeriod);
      if (period && period.start_date) {
        // Use period start date as default, but not before today if period is current
        const periodStart = new Date(period.start_date);
        const today = new Date();
        defaultDate = today > periodStart ? today.toISOString().split('T')[0] : periodStart.toISOString().split('T')[0];
      }
    }
    
    setTransferForm({
      ...transferForm,
      investor_id: investor.id,
      transfer_date: defaultDate
    });
    setTransferDialogOpen(true);
  };

  const handleDeleteClick = (investor) => {
    if (investor.balance !== 0) {
      toast.error(`Investor masih memiliki saldo ${formatCurrency(investor.balance)}. Saldo harus Rp 0 untuk menghapus.`);
      return;
    }
    setDeleteInvestor(investor);
    setDeletePassword('');
    setDeleteDialog(true);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      toast.error('Password harus diisi');
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/investors/${deleteInvestor.id}`, {
        data: { password: deletePassword }
      });
      toast.success('Investor berhasil dihapus');
      setDeleteDialog(false);
      setDeletePassword('');
      setDeleteInvestor(null);
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus investor');
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (investor) => {
    setSelectedInvestor(investor);
    setInvestorTransactions([]);
    setHistoryViewMode('last10');
    setHistoryFilter({ startDate: '', endDate: '' });
    setLoadingHistory(true);
    setHistoryDialogOpen(true);
    
    try {
      const res = await api.get(`/investors/${investor.id}/transactions`);
      setInvestorTransactions(res.data);
    } catch (error) {
      toast.error('Gagal mengambil history transaksi');
      setHistoryDialogOpen(false);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getFilteredInvestorTransactions = () => {
    let filtered = [...investorTransactions];
    // Sort oldest first (ascending) so running balance reads top→bottom
    filtered.sort((a, b) => new Date(a.created_at || a.transaction_date) - new Date(b.created_at || b.transaction_date));

    if (historyViewMode === 'last10') {
      return filtered.slice(-10);
    }
    if (historyFilter.startDate) {
      filtered = filtered.filter((tx) => new Date(tx.created_at || tx.transaction_date) >= new Date(historyFilter.startDate));
    }
    if (historyFilter.endDate) {
      filtered = filtered.filter((tx) => new Date(tx.created_at || tx.transaction_date) <= new Date(historyFilter.endDate + 'T23:59:59'));
    }
    return filtered;
  };

  const resetForm = () => {
    setFormData({ name: '', share_percentage: '', level: 1 });
    setEditingInvestor(null);
  };

  const resetTransferForm = () => {
    // Get default date based on selected period
    let defaultDate = new Date().toISOString().split('T')[0];
    
    if (selectedPeriod) {
      const period = periods.find(p => p.id === selectedPeriod);
      if (period && period.start_date) {
        const periodStart = new Date(period.start_date);
        const today = new Date();
        defaultDate = today > periodStart ? today.toISOString().split('T')[0] : periodStart.toISOString().split('T')[0];
      }
    }
    
    setTransferForm({
      investor_id: '',
      account_type: '',
      account_id: '',
      amount: '',
      transfer_type: 'DEPOSIT',
      description: '',
      transfer_date: defaultDate
    });
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    // Show up to 2 decimals when the value has a fractional part so any
    // non-integer balance is visible to the user.
    const hasFraction = Number(value) % 1 !== 0;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(value).replace('Rp', 'Rp.');
  };

  const canManageInvestors = canManageFinance(user);

  // dnd-kit sensors for drag-and-drop investor reordering
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Reorder handler: scoped per-level (drag only allowed within same level).
  const handleDragEnd = async (event, level) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const levelInvestorsList = investors
      .filter((inv) => (inv.level || 1) === level)
      .sort((a, b) => (a.order_number || 9999) - (b.order_number || 9999));

    const oldIndex = levelInvestorsList.findIndex((inv) => inv.id === active.id);
    const newIndex = levelInvestorsList.findIndex((inv) => inv.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(levelInvestorsList, oldIndex, newIndex);
    const updates = reordered.map((inv, idx) => ({ id: inv.id, order_number: idx + 1 }));

    // Optimistic UI update
    setInvestors((prev) =>
      prev.map((inv) => {
        const upd = updates.find((u) => u.id === inv.id);
        return upd ? { ...inv, order_number: upd.order_number } : inv;
      })
    );

    try {
      await api.put('/investors/reorder', { items: updates });
      toast.success('Urutan investor diperbarui');
    } catch (err) {
      console.error('Failed to reorder investors:', err);
      toast.error('Gagal mengurutkan investor');
      fetchInvestors();  // revert via refetch
    }
  };

  // Filter accounts by selected type
  const getAccountsByType = (type) => {
    return accounts.filter(acc => acc.type === type);
  };

  if (loading && investors.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Manajemen Investor</h1>
          <p className="text-slate-600 mt-1">Kelola investor dan kepemilikan saham</p>
        </div>
        {canManageInvestors && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <Plus size={20} className="mr-2" />
                Tambah Investor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingInvestor ? 'Edit Investor' : 'Tambah Investor Baru'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nama Investor *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="level">Level Kepemilikan Saham</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.level.toString()}
                      onValueChange={(value) => setFormData({ ...formData, level: parseInt(value) })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(maxLevel)].map((_, idx) => (
                          <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                            Level {idx + 1} {idx === 0 ? '(100%)' : `(Sisa Level ${idx})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setMaxLevel(prev => prev + 1)}
                      className="shrink-0"
                    >
                      <Plus size={14} />
                    </Button>
                    {maxLevel > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Check if any investor uses this level
                          const levelInUse = investors.some(inv => inv.level === maxLevel);
                          if (levelInUse) {
                            toast.error(`Level ${maxLevel} sedang digunakan oleh investor`);
                          } else {
                            setMaxLevel(prev => prev - 1);
                            if (formData.level === maxLevel) {
                              setFormData({ ...formData, level: maxLevel - 1 });
                            }
                          }
                        }}
                        className="shrink-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.level === 1 
                      ? 'Level 1: Langsung dari 100%'
                      : `Level ${formData.level}: Dari sisa Level ${formData.level - 1}`}
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="share_percentage">
                    Persentase Saham di Level {formData.level} (%) *
                  </Label>
                  <Input
                    id="share_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.share_percentage}
                    onChange={(e) => setFormData({ ...formData, share_percentage: e.target.value })}
                    placeholder="Contoh: 30"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Persentase dalam Level {formData.level} (maksimal 100% per level)
                  </p>
                </div>
                
                {!editingInvestor && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Catatan:</strong> Saldo awal investor adalah Rp 0. Gunakan menu Transfer untuk menambah atau menarik saldo.
                    </p>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="investor-submit-btn">
                    {loading ? 'Menyimpan...' : (editingInvestor ? 'Perbarui' : 'Simpan')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Helper function to calculate level remainders */}
      {(() => {
        // Calculate remaining for each level
        const sortedInvestors = [...investors].sort((a, b) => (a.level || 1) - (b.level || 1));
        let levelRemainders = {1: 100};
        
        const maxLvl = Math.max(...investors.map(inv => inv.level || 1), 1);
        for (let lvl = 1; lvl <= maxLvl; lvl++) {
          const levelInvestors = sortedInvestors.filter(inv => inv.level === lvl);
          const allocatedInLevel = levelInvestors.reduce((sum, inv) => sum + (inv.share_percentage || 0), 0);
          
          if (lvl < maxLvl) {
            const remainingFromLevel = levelRemainders[lvl] * (100 - allocatedInLevel) / 100;
            levelRemainders[lvl + 1] = remainingFromLevel;
          }
        }
        
        // Group investors by level
        const investorsByLevel = {};
        for (let lvl = 1; lvl <= maxLvl; lvl++) {
          investorsByLevel[lvl] = investors
            .filter(inv => inv.level === lvl)
            .sort((a, b) => {
              const ao = a.order_number ?? 9999;
              const bo = b.order_number ?? 9999;
              if (ao !== bo) return ao - bo;
              return (a.name || '').localeCompare(b.name || '');
            });
        }
        
        return (
          <div className="space-y-4">
            {/* Period Display - synced with global navigation */}
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full shadow-sm">
              <span className="text-white/90 text-sm font-medium">Periode</span>
              <span className="text-white text-base font-bold">
                {periods.find(p => p.id === selectedPeriod)?.name || '-'}
              </span>
              {periods.find(p => p.id === selectedPeriod)?.is_active && (
                <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-medium">Aktif</span>
              )}
              {periods.find(p => p.id === selectedPeriod)?.is_closed && (
                <span className="text-xs bg-red-400/80 text-white px-2 py-0.5 rounded-full font-medium">Ditutup</span>
              )}
            </div>

            {[...Array(maxLvl)].map((_, idx) => {
              const level = idx + 1;
              const levelInvestors = investorsByLevel[level] || [];
              const totalInLevel = levelInvestors.reduce((sum, inv) => sum + (inv.share_percentage || 0), 0);
              const remainingInLevel = 100 - totalInLevel;
              const baseForLevel = levelRemainders[level] || 0;

              // Per-investor Laba Berjalan computed at full decimal precision.
              // Decimals are now visible in the UI (formatCurrency shows 2 fraction
              // digits when present), so we no longer need to round per investor and
              // absorb the rounding diff into the biggest holder. The sum of the
              // exact decimal values equals totalDividenInvestor naturally.
              const labaBerjalanById = {};
              levelInvestors.forEach(inv => {
                const eff = ((inv.share_percentage || 0) / 100) * baseForLevel;
                labaBerjalanById[inv.id] = (eff / 100) * totalDividenInvestor;
              });
              
              return (
                <Card key={level} className="border-2 border-purple-200">
                  <CardHeader className={`pb-3 ${
                    level === 1 ? 'bg-purple-50' : 
                    level === 2 ? 'bg-blue-50' : 
                    'bg-teal-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold">
                        Level {level} Investors
                      </CardTitle>
                      <div className="text-right text-sm">
                        <div className="font-semibold text-purple-700">
                          Basis: {baseForLevel.toFixed(2)}% dari total
                        </div>
                        <div className="text-slate-600">
                          Dialokasikan: {totalInLevel.toFixed(2)}% • 
                          Sisa: <span className={`font-bold ${remainingInLevel < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {remainingInLevel.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {levelInvestors.length === 0 ? (
                      <p className="text-center text-slate-400 italic py-8">Belum ada investor di level ini</p>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleDragEnd(event, level)}
                      >
                        <Table className="text-xs [&_th]:whitespace-nowrap [&_th]:px-2 [&_th]:py-2 [&_th]:text-xs [&_td]:whitespace-nowrap [&_td]:px-2 [&_td]:py-1.5">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nama Investor</TableHead>
                              <TableHead className="text-right">Saldo Awal Periode</TableHead>
                              <TableHead className="text-right">Penambahan Modal</TableHead>
                              <TableHead className="text-right">Penarikan Modal</TableHead>
                              <TableHead className="text-right">Saldo Akhir</TableHead>
                              <TableHead className="text-right">Share dalam Level (%)</TableHead>
                              <TableHead className="text-right">Share Efektif (%)</TableHead>
                              <TableHead className="text-right">Laba Berjalan</TableHead>
                              <TableHead className="text-right">Saldo Next Periode</TableHead>
                              {canManageInvestors && <TableHead className="text-right">Aksi</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <SortableContext
                              items={levelInvestors.map((inv) => inv.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {levelInvestors.map((investor) => {
                                const effectivePercentage = (investor.share_percentage / 100) * baseForLevel;
                                const labaBerjalan = labaBerjalanById[investor.id] || 0;
                                return (
                                  <SortableInvestorRow
                                    key={investor.id}
                                    investor={investor}
                                    effectivePercentage={effectivePercentage}
                                    labaBerjalan={labaBerjalan}
                                    canManageInvestors={canManageInvestors}
                                    formatCurrency={formatCurrency}
                                    onEdit={openEditDialog}
                                    onTransfer={openTransferDialog}
                                    onDelete={handleDeleteClick}
                                    onViewHistory={handleViewHistory}
                                  />
                                );
                              })}
                            </SortableContext>
                          {/* Level Total Row */}
                          <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                            <TableCell className="font-bold text-blue-900">TOTAL LEVEL {level}</TableCell>
                            <TableCell className="text-right font-bold text-blue-900">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + (inv.opening_balance || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-green-700">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + (inv.period_deposits || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-red-700">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + (inv.period_withdrawals || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-blue-900">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + (inv.closing_balance || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-blue-900">
                              {totalInLevel.toFixed(2)}%
                            </TableCell>
                            <TableCell className="text-right font-bold text-blue-900">
                              {levelInvestors.reduce((sum, inv) => {
                                const eff = (inv.share_percentage / 100) * baseForLevel;
                                return sum + eff;
                              }, 0).toFixed(2)}%
                            </TableCell>
                            <TableCell className="text-right font-bold text-blue-900">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + Math.trunc(labaBerjalanById[inv.id] || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-purple-700">
                              {formatCurrency(levelInvestors.reduce((sum, inv) => sum + (inv.closing_balance || 0) + Math.trunc(labaBerjalanById[inv.id] || 0), 0))}
                            </TableCell>
                            {canManageInvestors && <TableCell></TableCell>}
                          </TableRow>
                        </TableBody>
                      </Table>
                      </DndContext>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })()}

      {/* Grand Total Summary */}
      {investors.length > 0 && (() => {
        // Calculate total effective share
        const sortedInvs = [...investors].sort((a, b) => a.level - b.level);
        let levelRemainders = {1: 100};
        const maxLvl = Math.max(...investors.map(inv => inv.level || 1), 1);
        
        for (let lvl = 1; lvl <= maxLvl; lvl++) {
          const levelInvs = sortedInvs.filter(inv => inv.level === lvl);
          const allocatedInLevel = levelInvs.reduce((sum, inv) => sum + (inv.share_percentage || 0), 0);
          
          if (lvl < maxLvl) {
            levelRemainders[lvl + 1] = levelRemainders[lvl] * (100 - allocatedInLevel) / 100;
          }
        }
        
        const totalEffective = investors.reduce((sum, inv) => {
          const level = inv.level || 1;
          const baseForLevel = levelRemainders[level] || 100;
          return sum + (inv.share_percentage / 100) * baseForLevel;
        }, 0);
        
        const isComplete = totalEffective >= 99.99 && totalEffective <= 100.01;
        
        return (
          <Card className={`border-2 ${isComplete ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Total Saldo Investor - NOW FIRST */}
                <div className="text-center">
                  <p className="text-sm text-slate-600 mb-2">Total Saldo Investor</p>
                  <p className="text-4xl font-bold text-blue-700">
                    {formatCurrency(investors.reduce((sum, inv) => sum + (inv.balance || 0), 0))}
                  </p>
                </div>
                
                {/* Total Share Efektif - NOW SECOND with conditional color */}
                <div className={`text-center border-l-2 ${isComplete ? 'border-green-300' : 'border-red-300'}`}>
                  <p className="text-sm text-slate-600 mb-2">Total Share Efektif Keseluruhan</p>
                  <p className={`text-4xl font-bold ${isComplete ? 'text-green-700' : 'text-red-600'}`}>
                    {totalEffective.toFixed(2)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {investors.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <Users size={64} className="mx-auto mb-4 opacity-20" />
            <p className="text-lg">Belum ada investor yang ditambahkan</p>
          </CardContent>
        </Card>
      )}

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={(open) => { setTransferDialogOpen(open); if (!open) resetTransferForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Investor</DialogTitle>
            {selectedPeriod && (
              <p className="text-sm text-teal-600 font-medium">
                Periode: {periods.find(p => p.id === selectedPeriod)?.name || '-'}
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleTransferSubmit} className="space-y-4">
            <div>
              <Label>Tanggal Transfer *</Label>
              <Input
                type="date"
                value={transferForm.transfer_date}
                onChange={(e) => setTransferForm({...transferForm, transfer_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Jenis Transfer *</Label>
              <Select 
                value={transferForm.transfer_type} 
                onValueChange={(value) => setTransferForm({...transferForm, transfer_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEPOSIT">
                    <div className="flex items-center gap-2">
                      <ArrowUpCircle size={16} className="text-green-600" />
                      Deposit (Tambah Saldo)
                    </div>
                  </SelectItem>
                  <SelectItem value="WITHDRAW">
                    <div className="flex items-center gap-2">
                      <ArrowDownCircle size={16} className="text-red-600" />
                      Withdraw (Tarik Saldo)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {transferForm.transfer_type === 'DEPOSIT' 
                  ? 'Investor menyetor uang ke rekening (saldo rekening bertambah)'
                  : 'Investor menarik uang dari rekening (saldo rekening berkurang)'}
              </p>
            </div>

            <div>
              <Label>Tipe Rekening *</Label>
              <Select 
                value={transferForm.account_type} 
                onValueChange={(value) => setTransferForm({...transferForm, account_type: value, account_id: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih tipe rekening..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CENTRAL">Pusat</SelectItem>
                  <SelectItem value="BRANCH">Cabang</SelectItem>
                  <SelectItem value="HUTANG_USAHA">Hutang Usaha</SelectItem>
                  <SelectItem value="MANAGER">Laba Rugi Mitra</SelectItem>
                  <SelectItem value="CUSTOMER">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {transferForm.account_type && (
              <div>
                <Label>Rekening *</Label>
                <SearchableSelect
                  value={transferForm.account_id}
                  onValueChange={(value) => setTransferForm({...transferForm, account_id: value})}
                  options={getAccountsByType(transferForm.account_type).map(acc => ({
                    value: acc.id,
                    label: `${acc.name} (${formatCurrency(acc.balance)})`
                  }))}
                  placeholder=""
                  searchPlaceholder=""
                  emptyText="Rekening tidak ditemukan"
                />
              </div>
            )}

            <div>
              <Label>Jumlah (Rp) *</Label>
              <Input
                type="text"
                value={transferForm.amount ? parseFloat(transferForm.amount).toLocaleString('id-ID') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d]/g, '');
                  setTransferForm({...transferForm, amount: value});
                }}
                required
              />
            </div>

            <div>
              <Label>Keterangan *</Label>
              <Input
                value={transferForm.description}
                onChange={(e) => setTransferForm({...transferForm, description: e.target.value})}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>
                Batal
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className={transferForm.transfer_type === 'DEPOSIT' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="investor-transfer-submit-btn"
              >
                {loading ? 'Memproses...' : (transferForm.transfer_type === 'DEPOSIT' ? 'Deposit' : 'Withdraw')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Investor History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-base" data-testid="history-dialog-title">History Transaksi: {selectedInvestor?.name}</DialogTitle>
          </DialogHeader>

          {/* View mode: Last 10 (default) or Tanggal (date range) */}
          <div className="mb-3">
            <RadioGroup
              value={historyViewMode}
              onValueChange={(val) => {
                setHistoryViewMode(val);
                if (val === 'last10') setHistoryFilter({ startDate: '', endDate: '' });
              }}
              className="flex flex-row gap-6"
              data-testid="history-view-mode-radio"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="last10" id="inv-history-mode-last10" data-testid="history-mode-last10" />
                <Label htmlFor="inv-history-mode-last10" className="text-xs cursor-pointer">10 Terakhir</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="date" id="inv-history-mode-date" data-testid="history-mode-date" />
                <Label htmlFor="inv-history-mode-date" className="text-xs cursor-pointer">Tanggal</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Date Filter - only visible in "Tanggal" mode */}
          {historyViewMode === 'date' && (
            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="history-date-filter">
              <div>
                <Label className="text-xs">Dari Tanggal</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={historyFilter.startDate}
                  onChange={(e) => setHistoryFilter({ ...historyFilter, startDate: e.target.value })}
                  data-testid="history-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">Sampai Tanggal</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={historyFilter.endDate}
                  onChange={(e) => setHistoryFilter({ ...historyFilter, endDate: e.target.value })}
                  data-testid="history-end-date"
                />
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setHistoryFilter({ startDate: '', endDate: '' })}
                  className="h-8 text-xs"
                  data-testid="history-reset-filter"
                >
                  Reset Filter
                </Button>
              </div>
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto relative [&>div]:overflow-visible">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Tgl. Input</TableHead>
                    <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Tgl. Transaksi</TableHead>
                    <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Tipe</TableHead>
                    <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Keterangan</TableHead>
                    <TableHead className="text-right h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Jumlah</TableHead>
                    <TableHead className="text-right h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredInvestorTransactions().map((tx) => {
                    const isPos = tx.transaction_type === 'DEPOSIT' || (tx.transaction_type !== 'WITHDRAW' && (tx.amount || 0) >= 0);
                    return (
                      <TableRow key={tx.id}>
                        <TableCell className="py-0.5 text-xs">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('id-ID') : '-'}</TableCell>
                        <TableCell className="py-0.5 text-xs">{tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('id-ID') : '-'}</TableCell>
                        <TableCell className="py-0.5">
                          <Badge
                            variant={isPos ? 'default' : 'destructive'}
                            className={`text-[10px] px-1.5 py-0 ${isPos ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          >
                            {tx.transaction_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-0.5 text-xs">{tx.description || '-'}</TableCell>
                        <TableCell className={`text-right py-0.5 text-xs font-semibold ${isPos ? 'text-green-600' : 'text-red-600'}`}>
                          {isPos ? '+' : '-'}{formatCurrency(Math.abs(tx.amount || 0))}
                        </TableCell>
                        <TableCell className="text-right py-0.5 text-xs font-semibold">{formatCurrency(tx.balance_after)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {getFilteredInvestorTransactions().length === 0 && !loadingHistory && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 italic py-4 text-sm">Belum ada transaksi</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Account Summary at Bottom */}
          {selectedInvestor && (
            <div className="mt-4 pt-4 border-t-4 border-slate-300">
              <div className="text-right">
                <span className="text-sm font-semibold text-slate-700">Sisa Saldo {selectedInvestor.name} : </span>
                <span className={`text-base font-bold ml-2 ${
                  getFilteredInvestorTransactions().length > 0
                    ? (getFilteredInvestorTransactions()[getFilteredInvestorTransactions().length - 1].balance_after >= 0 ? 'text-green-600' : 'text-red-600')
                    : 'text-slate-600'
                }`}>
                  {getFilteredInvestorTransactions().length > 0
                    ? formatCurrency(getFilteredInvestorTransactions()[getFilteredInvestorTransactions().length - 1].balance_after)
                    : 'Rp.\u00a00'}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus Investor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Anda akan menghapus investor: <span className="font-semibold">{deleteInvestor?.name}</span>
            </p>
            <p className="text-sm text-red-600">
              Tindakan ini tidak dapat dibatalkan. Masukkan password Anda untuk konfirmasi.
            </p>
            <form onSubmit={handleDelete} className="space-y-4">
              <div>
                <Label className="text-sm">Password *</Label>
                <Input
                  type="password"
                  required
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Masukkan password Anda"
                />
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setDeleteDialog(false);
                    setDeletePassword('');
                    setDeleteInvestor(null);
                  }}
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  variant="destructive"
                  disabled={loading}
                >
                  Hapus
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Investors;
